import { action, makeObservable, observable } from 'mobx'
import * as R from 'ramda'
import { cloneDeep, capitalize } from 'lodash'
import {
    reorder, isEmpty, guidGenerator, selectUpdater,
} from '../helpers'
import ENUM from './ENUM'
import * as QO from './configFormOptions'
import * as Slide from './model/slide'

import {
    CountryListQuestion,
    DropdownListQuestion,
    GridQuestion,
    SmartGridQuestion,
    MultipleChoiceQuestion,
    NumericQuestion,
    PercentageQuestion,
    PriorityQuestion,
    RatingQuestion,
    SingleChoiceQuestion,
    StaticText,
    TextAreaQuestion,
    TextBoxQuestion,
    UrlVariableQuestion,
    TextBoxListQuestion,
    FormQuestion,
    NumberFormQuestion,
    DateQuestion,
} from '../../Models/Question'

import configFormInputType from './configFormOptions/configFormInputType'
import ChartType from './model/CharType'
import RankType from './model/RankType'
import LoyaltyMatrixSlide from './model/slide/LoyaltyMatrixSlide'
import DerivedImportanceSlide from './model/slide/DerivedImportanceSlide'
import InheritedQuestionSlide from './model/slide/InheritedQuestionSlide'
import CrossBreakSlide from './model/slide/CrossBreakSlide'
import OverallScoreSlide from './model/slide/OverallScoreSlide'
import SubQuestion from './model/slide/SubQuestion'

class PresentationStore {
    selectLabels = [
        { name: 'chartType', label: 'Chart selection' },
        { name: 'ranking', label: 'Order by' },
    ]

    @observable globalYears = []

    @observable globalBenchmarks = []

    kpis = {
        OS: false,
        FA: false,
        NPS: false,
        IOV: false,
    }

    oidMap = {
        industry: 1,
        company: 3,
        division: 4,
        portfolio: 5,
        sector: 1,
    }

    disllowedQuestionTypes = new Set(['ST', 'I', 'TX', 'TA', 'UV', 'TL', 'DT', 'NF', 'FM'])

    @observable submitStatus = null

    @observable res = null

    @observable openSlides

    @observable benchmarkPeriod = 36

    @observable slideConfigs

    inheritedOptions = []

    derivedOptions = []

    kpiOptions = []

    @observable advancedSlidesItems = new Set()

    @observable state = ['completed']

    @observable showAdvancedSlidesModal = false

    constructor() {
        makeObservable(this)
    }

    benchmarkPeriodUpdater = ({ value }) => {
        this.benchmarkPeriod = value
    }

    toggleAdvancedSlidesModal = () => {
        this.advancedSlidesItems.clear()
        this.showAdvancedSlidesModal = !this.showAdvancedSlidesModal
    }

    getSelectLabel = (name) => {
        let label
        switch (name) {
            case 'chartType':
                label = 'Chart selection'
                break
            case 'ranking':
                label = 'Order by'
                break
            default: label = ''
        }
        return label
    }

    setQuestionInheritance = (store) => {
        const { survey } = store
        const inheritableOptions = {}
        return survey.pages.reduce((pageQuestions, page) => (
            pageQuestions.concat(R.pathOr([], ['questions'], page).slice().map((pq) => {
                const { parentId, id, question } = pq
                const maybeParentOptions = R.pathOr(null, [R.or(parentId, '').toUpperCase()], inheritableOptions)

                if (!R.isNil(maybeParentOptions)) {
                    const optionSet = R.hasPath(['optionSetY'], question)
                        ? question.optionSetY
                        : question.optionSet

                    optionSet.options = maybeParentOptions.concat(optionSet.options)
                }

                if (R.hasPath(['optionSet', 'options'], question)) {
                    inheritableOptions[id.toUpperCase()] = question.optionSet.options
                        .slice().filter(({ isNonReporting }) => !isNonReporting)
                }

                return pq
            }))
        ), [])
    }

    toQuestion = (data) => {
        const { question } = data
        const questionType = R.pathOr(null, ['questionTypeCode'], question)
        switch (questionType) {
            case 'CH':
                return new MultipleChoiceQuestion(question)
            case 'CO':
                return new CountryListQuestion(question)
            case 'I':
                return new NumericQuestion(question)
            case 'OD':
                return new PriorityQuestion(question)
            case 'PC':
                return new PercentageQuestion(question)
            case 'RD':
                return new SingleChoiceQuestion(question)
            case 'UV':
                return new UrlVariableQuestion(question)
            case 'TL':
                return new TextBoxListQuestion(question)
            case 'FM':
                return new FormQuestion(question)
            case 'NF':
                return new NumberFormQuestion(question)
            case 'DT':
                return new DateQuestion(question)
            case 'RT':
                return new RatingQuestion(question)
            case 'SB':
                return new DropdownListQuestion(question)
            case 'ST':
                return new StaticText(question)
            case 'TA':
                return new TextAreaQuestion(question)
            case 'TB':
                return new GridQuestion(question)
            case 'SG':
                return new SmartGridQuestion(question)
            case 'TX':
                return new TextBoxQuestion(question)
            default:
                return null
        }
    }

    getSlideQuestions = async () => {
        const { store } = this
        const { survey } = store

        const questionsResponse = await store.http.get(`/api/survey/${survey.id}/questions`)
        const questions = questionsResponse.reduce((map, data) => {
            const question = this.toQuestion(data)
            return R.isNil(question) ? map : Object.assign(map, {
                [question.id.toUpperCase()]: question,
            })
        }, {})

        return survey.standardPages.map((page) => (
            page.questions.map((pageQuestion) => {
                const { questionId } = pageQuestion
                pageQuestion.question = questions[questionId.toUpperCase()]
                return pageQuestion
            })
        ))
            .flat(2)
            .filter(({ question }) => !this.disllowedQuestionTypes.has(R.pathOr('ST', ['questionTypeCode'], question)))
    }

    getQuestionYears = async () => {
        const { store, surveyYear } = this
        const { survey } = store
        const questionYears = await store.http.get(`/survey/${survey.id}/yearsbenchmarks`)

        return questionYears.reduce((map, { questionId, surveyid_yr }) => {
            const years = surveyid_yr.split(',').map((yearInfo) => {
                const [surveyId, year] = yearInfo.split('_')
                return {
                    label: Number(year),
                    value: surveyId,
                    checked: false,
                }
            }).filter(({ label }) => label !== surveyYear)

            return Object.assign(map, {
                [questionId]: R.sortWith(
                    [R.descend(R.prop('label'))],
                    years,
                ),
            })
        }, {})
    }

    getGlobalBenchmarks = (slideQuestions, benchmarkSet) => {
        const { store, oidMap } = this
        const {
            availableBenchmarks,
        } = benchmarkSet

        /** This can be used if we want to have similar naming as in reports */
        // const benchmarkLabel = (benchmark) => {
        //     let label = i18n.t(`report_${benchmark}_benchmark`)

        //     if (benchmark === 'sector') {
        //         label += R.isNil(sectorName) ? '' : ` (${sectorName})`
        //     } else if (benchmark === 'portfolio') {
        //         label += R.isNil(portfolioName) ? '' : ` (${portfolioName})`
        //     } else if (benchmark === 'division') {
        //         label += R.isNil(divisionName) ? '' : ` (${divisionName})`
        //     }
        //     return label
        // }

        const questionOids = Array.from(slideQuestions.reduce((set, { question }) => (
            set.add(question.metadata.oid)
        ), new Set()))

        const benchmarks = Object.entries(availableBenchmarks)
            .filter(([benchmark, isAvailable]) => (
                isAvailable
                    && R.pathOr(false, ['userData', 'benchmarks', benchmark], store)
                    && (
                        (['industry', 'sector'].includes(benchmark) && questionOids.some((oid) => oid === 1))
                        || (benchmark !== 'industry' && questionOids.some((oid) => oid <= oidMap[benchmark]))
                    )
            ))
            .map(([benchmark]) => ({
                name: ENUM().benchmarks,
                value: benchmark,
                checked: false,
                oid: benchmark === 'sector' ? 6 : oidMap[benchmark],
                label: capitalize(benchmark),
            }))

        return R.sortWith(
            [R.ascend(R.prop('oid'))],
            benchmarks,
        )
    }

    getBenchmarks = async () => {
        const { store } = this
        const { survey } = store

        let benchmarks = {
            availableBenchmarks: {},
        }

        try {
            benchmarks = await store.http.get(`/v2/survey/${survey.id}/report/benchmark?period=ROL&duration=36`)
        } catch (e) {
            console.error(e)
            store.addNotification(null, 'Error loading benhcmarks', 'error')
        }

        return benchmarks
    }

    presentationInitialState = async (state, store) => {
        this.store = store
        this.surveyYear = this.getSurveyYear()

        const [slideQuestions, globalYears, benchmarkSet, questionYears] = await Promise.all([
            this.getSlideQuestions(),
            this.getGlobalYears(),
            this.getBenchmarks(),
            this.getQuestionYears(),
        ])
        const globalBenchmarks = this.getGlobalBenchmarks(slideQuestions, benchmarkSet)

        this.slideConfigs = await this.createSlideConfigs(
            slideQuestions,
            globalBenchmarks,
            globalYears,
            questionYears,
            benchmarkSet,
        )

        this.openSlides = this.slideConfigs.reduce((map, { slide }) => (
            map.set(slide.id, false)
        ), new Map())

        // this.getSlideOptions()

        this.globalYears = globalYears
        this.globalBenchmarks = globalBenchmarks
    }

    doesSlideExist = (questionTypeCode, slideConfigs) => slideConfigs
        .find((item) => item.question.questionTypeCode === questionTypeCode)

    getShortTitle = (question, store) => {
        const { langcode } = store
        const { defaultLocale } = store.survey.metadata

        return question.content.getTitleByLocale('shortTitle', langcode, defaultLocale)
    }

    createSlideConfigs = async (
        slideQuestions,
        globalBenchmarks,
        globalYears,
        questionYears,
        benchmarkSet,
    ) => {
        const { store } = this

        this.crossBreakSubQuestionOptions = slideQuestions
            .filter(({ question }) => ['RD', 'CH', 'SB'].includes(question.questionTypeCode))
            .map(({ question, id }) => ({
                name: ENUM().pageQuestionId,
                value: id,
                label: this.getShortTitle(question, store),
                isDisabled: false,
                userSelected: false,
            }))

        this.kpiOptions = slideQuestions
            .filter(({ question }) => (
                ['RD', 'SB'].includes(question.questionTypeCode)
                && question.optionSet.hasNumericCodes
            ))
            .map(({ question, id }) => ({
                name: ENUM().kpiPageQuestionId,
                value: id,
                label: this.getShortTitle(question, store),
            }))

        const getParentQuestion = (pageQuestionId) => {
            const mayabePageQuestion = slideQuestions
                .find(({ id }) => id === pageQuestionId)

            return R.pathOr(null, ['question'], mayabePageQuestion)
        }

        const slideConfigs = slideQuestions
            .map(({ parentId, id: pageQuestionId, question }) => {
                const { questionTypeCode, id: questionId, metadata } = question
                const { kpiType } = metadata

                if (questionTypeCode === 'TB') {
                    if (!R.isNil(parentId)) {
                        const parentPageQuestion = getParentQuestion(parentId)

                        if (parentPageQuestion) {
                            const obj = {
                                name: ENUM().childPageQuestionId,
                                parentLabel: this.getShortTitle(parentPageQuestion, store),
                                value: pageQuestionId,
                                label: this.getShortTitle(question, store),
                            }
                            this.inheritedOptions.push(obj)
                        }
                    }
                    if (question.optionSetX.hasNumericCodes) {
                        const obj = {
                            name: ENUM().pageQuestionId,
                            value: pageQuestionId,
                            label: this.getShortTitle(question, store),
                        }
                        this.derivedOptions.push(obj)
                    }
                }

                let hasLockedOptions = false

                if (question.isSingleAxis()) {
                    const lockedOptions = (ele) => ele.isLocked
                    hasLockedOptions = question.optionSet.options.some(lockedOptions)
                }
                if (question.isDoubleAxis()) {
                    const lockedOptions = (ele) => ele.isLocked
                    hasLockedOptions = question.optionSetX.options.some(lockedOptions)
                        || question.optionSetY.options.some(lockedOptions)
                }

                const references = {
                    questionId,
                    pageQuestionId,
                    [ENUM().kpiPageQuestionId]: this.kpiOptions.find(({ value }) => value === pageQuestionId),
                }

                let hasNumericCodes = false
                if (questionTypeCode === 'RD') {
                    hasNumericCodes = !!(question.optionSet.hasNumericCodes)
                    if (!isEmpty(question.metadata.kpiType)) {
                        const { kpiType } = question.metadata
                        this.kpis[kpiType] = true
                    }
                }
                let hasNonNumericValues = false
                if (questionTypeCode === 'TB') {
                    hasNumericCodes = !!(question.optionSetX.hasNumericCodes)
                    const nonNumericOptions = (ele) => !ele.hasOwnProperty('reportingValue')
                    hasNonNumericValues = question.optionSetX.options.some(nonNumericOptions)
                }

                const questionBenhcmarks = this.getQuestionBenchmarks(question.id, globalBenchmarks, benchmarkSet)
                const chartTypes = ChartType.getOptions(questionTypeCode, kpiType, hasNumericCodes)
                const rankTypes = RankType.getOptions()
                const slide = Slide.questionToSlide(
                    pageQuestionId,
                    question,
                    questionBenhcmarks,
                    questionYears[questionId],
                    chartTypes[0].value,
                    rankTypes[0].value,
                )

                return {
                    question,
                    powerPointObj: this.buildPowerpointObj(questionTypeCode, references, question),
                    hasNumericCodes,
                    hasNonNumericValues,
                    hasLockedOptions,
                    slide,
                    isDoubleAxis: question.isSingleAxis(),
                    isSingleAxis: question.isDoubleAxis(),
                }
            })

        return slideConfigs
    }

    updatePowerPointObj = ({
        name,
        value,
        config:
        {
            powerPointObj,
            yearsQuestionWasAsked,
        },
    }) => {
        const inputValues = powerPointObj[name]
        const objRef = powerPointObj
        if (Array.isArray(inputValues)) {
            if (Array.isArray(yearsQuestionWasAsked) && name === 'comparisonSurveyIds') {
                if (yearsQuestionWasAsked.find((item) => item.value === value)) {
                    if (!inputValues.includes(value)) {
                        inputValues.push(value)
                    } else {
                        const index = inputValues.indexOf(value)
                        inputValues.splice(index, 1)
                    }
                }
            } else if (!inputValues.includes(value)) {
                inputValues.push(value)
            } else {
                const index = inputValues.indexOf(value)
                inputValues.splice(index, 1)
            }
        } else if (typeof inputValues === 'boolean' || name === 'excludeNonNumeric') {
            objRef[name] = !objRef[name]
        } else if (Object.prototype.hasOwnProperty.call(objRef, name)) {
            objRef[name] = value
        }
        return objRef
    }

    getSurveyYear = () => {
        const { store } = this
        const { survey } = store

        const endDate = R.pathOr(null, ['metadata', 'productInfo', 'industrySpecificInfo', 'endDate'], survey)
        const closeDate = R.pathOr(null, ['metadata', 'closeDate'], survey)

        return new Date(R.or(endDate, closeDate)).getFullYear()
    }

    getGlobalYears = async () => {
        const { store, surveyYear } = this
        const { survey } = store
        const { productId } = survey.metadata.productInfo
        const { surveyType, respondentType } = survey.metadata.taxonomy
        const allSurveyYears = await store.http.get(`/product/${productId}/surveyyears/${surveyType}/${respondentType}`)

        if (isEmpty(allSurveyYears)) {
            throw new Error('No results')
        }

        const mappedYears = allSurveyYears
            .filter(({ year }) => (
                Number(year) !== surveyYear
                && !Number.isNaN(Number(year))
            ))
            .reduce((map, { year, surveyId }) => (
                !Object.hasOwnProperty.call(map, year)
                    ? Object.assign(map, {
                        [year]: {
                            name: ENUM().comparisonSurveyIds,
                            value: surveyId.toUpperCase(),
                            label: String(year),
                            year: Number(year),
                            checked: false,
                        },
                    })
                    : map
            ), {})

        return R.sortWith(
            [R.descend(R.prop('year'))],
            Object.values(mappedYears),
        ).slice(0, 5)
    }

    @action
    globalBenchmarkSettingUpdater = ({ name, value }) => {
        this.globalBenchmarks = this.globalBenchmarks.slice().map((benchmark) => {
            if (benchmark.value === name) {
                benchmark.checked = value
            }
            return benchmark
        })

        this.slideConfigs.forEach(({ slide }) => {
            slide.benchmarks = slide.benchmarks
                .slice()
                .map((benchmark) => {
                    if (benchmark.value === name) {
                        benchmark.checked = value
                    }
                    return benchmark
                })
        })
    }

    @action
    globalYearSettingUpdater = (({ name, value }) => {
        this.globalYears = this.globalYears.slice().map((year) => {
            if (year.value === name) {
                year.checked = value
            }
            return year
        })

        console.log('globalYearSettingUpdater', name, value)
        this.slideConfigs.forEach(({ slide }) => {
            slide.comparisonSurveyIds = R.pathOr([], ['comparisonSurveyIds'], slide)
                .slice()
                .map((year) => {
                    if (year.value === name) {
                        year.checked = value
                    }
                    return year
                })
        })
    })

    getConfigFormOptions = (questionTypeCode, question, references) => {
        let data = null
        if (isEmpty(questionTypeCode)) {
            throw new Error('No questionTypeCode was passed')
        } else {
            switch (questionTypeCode) {
                case 'CH':
                case 'ST':
                case 'SB':
                case 'RD':
                    data = QO.RD
                    break
                case 'NPS':
                    data = QO.NPS
                    break
                case 'OD':
                case 'PC':
                    data = QO.OD
                    break
                case 'OSS':
                    data = QO.OSS
                    break
                case 'LMX':
                    data = QO.LMX
                    break
                case 'KPI':
                    data = QO.KPI
                    break
                case 'TB':
                    data = QO.TB
                    break
                case 'RT':
                    data = QO.RT
                    break
                case 'TX':
                    data = QO.TX
                    break
                case 'TA':
                    data = QO.TA
                    break
                case 'SG':
                    data = QO.SG
                    break
                case 'Inherited':
                    data = QO.Inherited
                    break
                case 'Derived':
                    data = QO.Derived
                    break
                case 'CrossBreak':
                    data = QO.CrossBreak
                    break
                default:
                    break
            }
            /**
             * .cloneDeep returns a new object with no reference
             */
            return (typeof data === 'function') ? data(question, R.pathOr(null, ['pageQuestionId'], references), this.globalBenchmarks) : cloneDeep(data)
        }
    }

    getQuestionBenchmarks = (questionId, globalBenchmarks, benchmarkSet) => {
        const { benchmarks } = benchmarkSet

        const returnedBenchmarks = new Set(
            Object.keys(R.pathOr({}, [questionId], benchmarks)),
        )

        return globalBenchmarks
            .filter(({ value }) => returnedBenchmarks.has(value))
            .map((b) => cloneDeep(b))
    }

    buildPowerpointObj = (questionTypeCode, references, question) => {
        const questionOptions = this.getConfigFormOptions(questionTypeCode, question, references)
        const powerPointObj = {}
        powerPointObj[ENUM().kind] = (!isEmpty(questionOptions[ENUM().kind]))
            ? questionOptions[ENUM().kind]
            : null
        if (!isEmpty(questionOptions.filters)) {
            questionOptions.filters.forEach((filter) => {
                if (
                    filter.name === ENUM().comparisonSurveyIds
                    || filter.name === ENUM().benchmarks
                    || filter.name === 'questions'
                ) {
                    powerPointObj[filter.name] = []
                } else if (filter.name === ENUM().locked) {
                    powerPointObj[filter.name] = false
                } else {
                    powerPointObj[filter.name] = null
                }
            })
        }
        if (!isEmpty(questionOptions.references)) {
            questionOptions.references.forEach((reference) => {
                powerPointObj[reference] = references[reference]
            })
        }
        powerPointObj.questionOptions = questionOptions
        return powerPointObj
    }

    addSlideConfig = (questionTypeCode, hasNumericCodes, isBenchmarked, references) => {
        const globalBenchmarks = this.globalBenchmarks.slice().map((element) => (
            {
                name: element.name,
                value: element.value,
                checked: false,
                label: element.label,
            }
        ))
        const globalYears = this.globalYears.slice().map((element) => (
            {
                name: element.name,
                value: element.value,
                checked: false,
                label: element.label,
            }
        ))

        console.log('addSlideConfig', questionTypeCode)

        let slide

        switch (questionTypeCode) {
            case 'LMX':
                slide = new LoyaltyMatrixSlide({
                    benchmarks: globalBenchmarks,
                    comparisonSurveyIds: globalYears,
                })
                break
            case 'Derived':
                slide = new DerivedImportanceSlide({
                    kpiPageQuestionId: this.kpiOptions[0].value,
                    pageQuestionId: this.derivedOptions[0].value,
                })
                break
            case 'OSS':
                slide = new OverallScoreSlide({
                    comparisonSurveyIds: globalYears,
                })
                break
            case 'Inherited':
                slide = new InheritedQuestionSlide({
                    childPageQuestionId: this.inheritedOptions[0].value,
                    comparisonSurveyIds: globalYears,
                })
                break
            case 'CrossBreak':
                slide = new CrossBreakSlide({
                    benchmarks: globalBenchmarks,
                    comparisonSurveyIds: globalYears,
                    kpiPageQuestionId: this.kpiOptions[0].value,
                    questions: [new SubQuestion({
                        pageQuestionId: this.crossBreakSubQuestionOptions[0].value,
                    })],
                })
                break
            default:
        }

        if (['Derived', 'LMX', 'Inherited', 'OSS', 'CrossBreak'].includes(questionTypeCode)) {
            const slideConfigs = this.slideConfigs.slice()
            const question = {
                questionTypeCode,
                metadata: {
                    kpiType: questionTypeCode,
                },
            }
            slideConfigs.push({
                question,
                powerPointObj: this.buildPowerpointObj(questionTypeCode, references, question),
                slide,
            })
            this.openSlides.set(slide.id, false)
            this.slideConfigs = slideConfigs
            console.log('slideConfigs', slideConfigs)
        }
    }

    removeSlideConfig = (index) => {
        const slideConfigs = this.slideConfigs.slice()
        const [config] = slideConfigs.splice(index, 1)
        this.openSlides.delete(config.slide.id)
        this.slideConfigs = slideConfigs
    }

    addSubQuestion = (slide) => {
        const existingQuestions = new Set(slide.questions.map(({ pageQuestionId }) => pageQuestionId))
        const pageQuestion = this.crossBreakSubQuestionOptions.find(({ value }) => !existingQuestions.has(value))

        if (pageQuestion) {
            slide.questions.push(new SubQuestion({
                pageQuestionId: pageQuestion.value,
            }))
        }
    }

    duplicateSlide = (slide, index) => {
        const newSlide = CrossBreakSlide.clone(slide)
        const originalConfig = this.slideConfigs[index]
        const config = {
            question: cloneDeep(originalConfig.question),
            powerPointObj: cloneDeep(originalConfig.powerPointObj),
            slide: newSlide,
        }
        const slideConfigs = this.slideConfigs.slice()

        config.isClone = true
        slideConfigs.splice(index + 1, 0, config)
        this.slideConfigs = slideConfigs
    }

    onDragEnd = (result, state) => {
        const copyOfState = { ...state }
        const { source, destination } = result
        this.slideConfigs = reorder(
            this.slideConfigs.slice(),
            source.index,
            destination.index,
        )
        return copyOfState
    }

    minimiseSlide = (id) => {
        this.openSlides.set(id, !this.openSlides.get(id))
    }

    minimiseAllHandler = (isOpen) => {
        this.openSlides = Array.from(this.openSlides).reduce((map, [k]) => (
            map.set(k, isOpen)
        ), new Map())
    }

    globalBenchmarksSettingUpdater = (value, benchmarkName, state) => {
        const typeOfGlobalSetting = 'globalBenchmarks'
        const copyOfState = cloneDeep(state)
        let slideConfigProperty
        switch (typeOfGlobalSetting) {
            case 'globalYears':
                slideConfigProperty = 'yearsQuestionWasAsked'
                break
            default:
                slideConfigProperty = ENUM().benchmarks
                break
        }
        copyOfState.slideConfigs.forEach((slideConfig) => {
            if (!isEmpty(slideConfig[slideConfigProperty])) {
                const found = slideConfig[slideConfigProperty]
                    .find((bench) => bench.label === benchmarkName)

                if (!isEmpty(found)) {
                    found.checked = value
                }
            }
        })
        let powerPointObjPropName
        switch (typeOfGlobalSetting) {
            case 'globalYears':
                powerPointObjPropName = ENUM().comparisonSurveyIds
                break
            default:
                powerPointObjPropName = ENUM().benchmarks
                break
        }
        copyOfState.slideConfigs.forEach((slideConfig) => {
            const slideConfigRef = slideConfig
            const args = {
                name: powerPointObjPropName,
                value: benchmarkName,
                config: {
                    powerPointObj: slideConfig.powerPointObj,
                    yearsQuestionWasAsked: slideConfig.yearsQuestionWasAsked,
                },
            }
            slideConfigRef.powerPointObj = this.updatePowerPointObj(args)
        })
        const foundSetting = copyOfState[typeOfGlobalSetting]
            .find((item) => item.value === benchmarkName)
        foundSetting.checked = value
        return copyOfState
    }

    globalYearsSettingUpdater = (value, name, state) => {
        const typeOfGlobalSetting = 'globalYears'
        const copyOfState = cloneDeep(state)
        let slideConfigProperty
        switch (typeOfGlobalSetting) {
            case 'globalYears':
                slideConfigProperty = 'yearsQuestionWasAsked'
                break
            default:
                slideConfigProperty = ENUM().benchmarks
                break
        }
        copyOfState.slideConfigs.forEach((slideConfig) => {
            if (!isEmpty(slideConfig[slideConfigProperty])) {
                const found = slideConfig[slideConfigProperty]
                    .find((year) => year.label === name)

                if (!isEmpty(found)) {
                    found.checked = value
                }
            }
        })

        let powerPointObjPropName
        switch (typeOfGlobalSetting) {
            case 'globalYears':
                powerPointObjPropName = ENUM().comparisonSurveyIds
                break
            default:
                powerPointObjPropName = ENUM().benchmarks
                break
        }

        const foundValue = copyOfState[typeOfGlobalSetting]
            .find((item) => item.label === name)
        const val = foundValue.value
        copyOfState.slideConfigs.forEach((slideConfig) => {
            const slideConfigRef = slideConfig
            const args = {
                name: powerPointObjPropName,
                val,
                config: {
                    powerPointObj: slideConfig.powerPointObj,
                    yearsQuestionWasAsked: slideConfig.yearsQuestionWasAsked,
                },
            }
            slideConfigRef.powerPointObj = this.updatePowerPointObj(args)
        })
        const foundSetting = copyOfState[typeOfGlobalSetting]
            .find((item) => item.label === name)
        foundSetting.checked = value
        return copyOfState
    }

    submitPowerpoint = async (pageQuestionId, includeQuestions) => {
        const { store } = this
        const data = {}
        data.locale = (!isEmpty(store.survey.metadata.defaultLocale)) ? store.survey.metadata.defaultLocale : 'en_GB'
        data.benchmarkPeriod = {
            periodType: 'ROL',
            duration: this.benchmarkPeriod,
        }
        data.surveyId = store.survey.id
        data.slideConfigs = this.slideConfigs.slice()
            .filter((slide) => !includeQuestions || includeQuestions.includes(slide.question.id))
            .map(({ slide }) => slide)
        const projectSlides = ['ProjectSpecSlide', 'ExecutiveSummarySlide', 'ExploriBenchmarksExplained', 'ExploriCorrelationExplained', 'NetPromoterExplainedSlide', 'ContactSlide']
        projectSlides.map((slideKind) => {
            const projectSlide = { kind: slideKind }
            data.slideConfigs.push(projectSlide)
        })

        if (!R.isNil(pageQuestionId)) {
            data.slideConfigs = data.slideConfigs.filter((slide) => (
                slide?.pageQuestionId?.toUpperCase() === pageQuestionId.toUpperCase()
            ))
        }

        const wsUrl = store.http.gatewayUrl.replace(/https:\/\//, 'wss://').replace(/http:\/\//, 'ws://')
        return new Promise((resolve) => {
            const socket = io(wsUrl, {
                query: `&target=export&method=export/pptxadvanced&token=${store.token}`,
            })

            socket.on('connection', (response) => {
                const request = {
                    ...data,
                    kind: 'powerPointExport',
                    userId: store.tokenData.userId,
                    sessionId: store.userData.userId,
                    state: this.state,
                    useCache: R.pathOr(false, ['userData', 'flags', 'useReportCache'], store),
                }
                console.log(`onConnection ${response}`)
                socket.emit('data', JSON.stringify(request))
            })
            socket.on('response', (data) => {
                console.log(`response ${data}`)
                socket.disconnect()

                if (data.match(/^http/gi)) {
                    this.submitStatus = 200
                } else {
                    this.submitStatus = 400
                }
                resolve({ resp: data, submitStatus: 200 })
            })

            socket.onerror = function onerror(error) {
                console.log(`onerror ${error}`)
                socket.disconnect()
                resolve({ resp: error, submitStatus: 500 })
            }
        })
    }

    buildCrossBreakSubQuestion = (isClone, props) => ({
        isClone,
        id: guidGenerator(),
        powerPointObj: props.config.powerPointObj,
        crossBreakSubQuestionOptions: props.crossBreakSubQuestionOptions,
        crossBreakSubRankingOptions:
            this
                .getConfigFormOptions(props.config.question.questionTypeCode).filters
                .find((item) => item.name === ENUM().ranking).options,
    })

    enableSubQuestion = (value, state, crossBreakSubQuestionOptions) => {
        const copyOfState = { ...state }
        const found = copyOfState.crossBreakSubQuestionOptions
            .find((item) => item.value === value)

        found.isDisabled = false
        found.userSelected = false
        return copyOfState
    }

    disableSubQuestion = (value, state, crossBreakSubQuestionOptions) => {
        const copyOfState = { ...state }
        const found = copyOfState.crossBreakSubQuestionOptions
            .find((item) => item.value === value)
        found.isDisabled = true
        found.userSelected = true
        return copyOfState
    }

    findCheckedAndConvertToChecked = (item, clonedArray) => {
        const itemRef = item
        if (clonedArray.includes(item.value)) {
            itemRef.checked = true
            return item
        }
        return item
    }

    populateComponent = (state, props, comparisonSurveyYears, benchmarks) => {
        const { config: { powerPointObj }, kpiOptions } = props
        const copyOfState = cloneDeep(state)
        const kpiOptionId = powerPointObj.kpiPageQuestionId
        copyOfState.selectedOption = kpiOptions.find((item) => item.value === kpiOptionId)

        const clonedYears = powerPointObj.comparisonSurveyIds
        const clonedBenchmarks = powerPointObj.benchmarks
        console.log('populateComponent', powerPointObj.questions)
        powerPointObj.questions.forEach(() => {
            const toAdd = this.buildCrossBreakSubQuestion(true, props)
            copyOfState.crossBreakQuestions.push(toAdd)
        })
        comparisonSurveyYears.map((item) => this
            .findCheckedAndConvertToChecked(item, clonedYears))
        benchmarks.map((item) => this
            .findCheckedAndConvertToChecked(item, clonedBenchmarks))

        return copyOfState
    }

    removeCrossBreakQuestion = (index, value, state, props, crossBreakSubQuestionOptions) => {
        let copyOfState = cloneDeep(state)
        const powerPointObjRef = props.config.powerPointObj
        copyOfState.crossBreakSubQuestionOptions[index].isDisabled = false
        copyOfState.crossBreakSubQuestionOptions[index].userSelected = false

        copyOfState.crossBreakQuestions = copyOfState.crossBreakQuestions
            .slice(0, index)
            .concat(copyOfState.crossBreakQuestions.slice(index + 1))
        powerPointObjRef.questions = powerPointObjRef.questions
            .slice(0, index)
            .concat(powerPointObjRef.questions.slice(index + 1))
        copyOfState = this.enableSubQuestion(value, copyOfState, copyOfState.crossBreakSubQuestionOptions)
        return copyOfState
    }

    onChangeSubQuestionHandler = (option, previousValue, state, props) => {
        let copyOfState = cloneDeep(state)
        copyOfState = this.enableSubQuestion(
            previousValue, copyOfState, copyOfState.crossBreakSubQuestionOptions,
        )
        copyOfState = this.disableSubQuestion(
            option.value, copyOfState, copyOfState.crossBreakSubQuestionOptions,
        )
        return copyOfState
    }

    addCrossBreakSubQuestion = (state, props) => {
        const copyOfState = { ...state }
        const toAdd = this.buildCrossBreakSubQuestion(false, props)
        copyOfState.crossBreakQuestions.push(toAdd)
        return copyOfState
    }

    disableSubQuestionAndSetState = (value, state) => {
        const copyOfState = { ...state }
        const found = copyOfState.crossBreakSubQuestionOptions
            .find((item) => item.value === value)
        found.isDisabled = true
        found.userSelected = false
        return copyOfState
    }

    onChangeHandler = (selectedOption, props, state) => {
        const updatableObj = props.config.powerPointObj
        this.selectUpdate(selectedOption, updatableObj)
        const copyOfState = { ...state }
        copyOfState.selectedOption = selectedOption
        return copyOfState
    }

    selectUpdate = (selectedOption, updatableObj) => {
        selectUpdater(
            selectedOption.value,
            updatableObj,
            selectedOption.name,
        )
    }

    onChangeHandlerSelect = (selectedOption, props) => {
        this.selectUpdate(selectedOption, props.config.powerPointObj)
    }

    setDefaultValuesInDerrived = (config, defaultValues) => {
        const objRef = config
        objRef.powerPointObj = this.updatePowerPointObj(
            {
                ...defaultValues.kpi,
                config: { ...config },
            },
        )
        objRef.powerPointObj = this.updatePowerPointObj(
            {
                ...defaultValues.derived,
                config: { ...config },
            },
        )
    }

    setDefaultSelectValues = (questionOptions, defaultSelectValues) => {
        questionOptions.filters.forEach((filter) => {
            const { inputType } = configFormInputType[filter.name]

            if (inputType === 'select') {
                let defaultValue
                const { options } = questionOptions.filters
                    .find((item) => item.name === filter.name)

                if (!isEmpty(options)) {
                    [defaultValue] = questionOptions.filters
                        .find((item) => item.name === filter.name).options

                    defaultSelectValues.push(defaultValue)
                }
            } else {
                defaultSelectValues.push({})
            }
        })
        return defaultSelectValues
    }

    setDefaultValuesOnMounting = (config, questionOptions) => {
        questionOptions.filters.forEach((filter) => {
            const { inputType } = configFormInputType[filter.name]
            if (inputType === 'select') {
                let defaultValue

                const { options } = questionOptions.filters
                    .find((item) => item.name === filter.name)
                if (!isEmpty(options)) {
                    [defaultValue] = questionOptions.filters
                        .find((item) => item.name === filter.name).options

                    const data = {
                        name: filter.name,
                        value: defaultValue.value,
                        config,
                    }
                    const objRef = config
                    objRef.powerPointObj = this.updatePowerPointObj(data)
                }
            }
        })
    }

    populateSubQuestionomponent = (state, props) => {
        const {
            crossBreakSubRankingOptions,
            questions,
            crossBreakSubQuestionOptions,
            index,
        } = props
        const copyOfState = { ...state }

        const chosenRank = questions[index].ranking
        const chosenBaseLimit = questions[index].removeBaseLimit
        const chosenOption = questions[index].pageQuestionId
        copyOfState.selectedRanking = crossBreakSubRankingOptions
            .find((item) => item.value === chosenRank)
        copyOfState.removeBaseLimit = chosenBaseLimit
        copyOfState.selectedOption = crossBreakSubQuestionOptions
            .find((item) => item.value === chosenOption)

        return copyOfState
    }

    onChangeCrossBreakOptionHandler = (selectedOption, state, props) => {
        const copyOfState = { ...state }
        copyOfState.selectedRanking = selectedOption
        const updatableObj = props.powerPointObj.questions[props.index]
        this.selectUpdate(selectedOption, updatableObj)
        return copyOfState
    }

    advancedSlidesProps = {
        CrossBreak: {
            questionTypeCode: 'CrossBreak',
            references: { [ENUM().kpiPageQuestionId]: undefined },
        },
        Derived: {
            questionTypeCode: 'Derived',
            references:
                {
                    questionId: undefined,
                    [ENUM().kpiPageQuestionId]: undefined,
                    removeBaseLimit: 5,
                },
        },
        Inherited: {
            questionTypeCode: 'Inherited',
            references: {
                parentId: undefined,
                childId: undefined,
            },
        },
        LMX: { questionTypeCode: 'LMX' },
        OSS: { questionTypeCode: 'OSS' },
    }

    advancedSlideOptions = []

    getAdvancedSlideOptions = () => {
        const {
            kpiOptions,
            derivedOptions,
            inheritedOptions,
            slideConfigs,
            kpis,
        } = this

        console.log('getAdvancedSlideOptions', inheritedOptions)

        return [
            {
                name: 'CrossBreak',
                isDisabled: (kpiOptions.length === 0),
                label: 'Add cross break slide',
            },
            {
                name: 'Derived',
                isDisabled: (derivedOptions.length === 0),
                label: 'Add derived slide',
            },
            {
                name: 'Inherited',
                isDisabled: (inheritedOptions.length === 0),
                label: 'Add inherited slide',
            },
            {
                name: 'LMX',
                isDisabled: ((!kpis.FA && !kpis.OS) || this.doesSlideExist('LMX', slideConfigs)),
                label: 'Add loyalty matrix slide',
            },
            {
                name: 'OSS',
                isDisabled: (
                    Object
                        .keys(this.kpis)
                        .some((i) => this.kpis[i] === false)
                || this.doesSlideExist('OSS', slideConfigs)),
                label: 'Add overall show score',
            },
        ].filter(({ isDisabled }) => !isDisabled)
    }

    getProps = (name) => {
        const props = {
            questionTypeCode: this.advancedSlidesProps[name].questionTypeCode,
            hasNumericCodes: null,
            isBenchmarked: null,
            references: this.advancedSlidesProps[name].references,
        }
        return props
    }

    @action
    resetGlobalSettings = () => {
        this.globalBenchmarks.forEach(({ value }) => {
            this.globalBenchmarkSettingUpdater({
                name: value,
                value: false,
            })
        })
        this.globalYears.forEach(({ value }) => {
            this.globalYearSettingUpdater({
                name: value,
                value: false,
            })
        })

        this.benchmarkPeriodUpdater({ value: 36 })
    }

    handleAdvancedModalClick = ({ value, name }) => {
        if (value) {
            this.advancedSlidesItems.add(name)
        } else {
            this.advancedSlidesItems.delete(name)
        }
    }

    updatePowerpointObjectCheckboxCols = (
        name,
        value,
        props,
        questionOptions,
    ) => {
        const { config } = props
        const objName = name.split(' - ')[0]
        const objValue = name.split(' - ')[1]
        let found
        if (objName === 'benchmarks') {
            found = config.benchmarks
                .find((bench) => bench.value === objValue)

            if (!isEmpty(found)) {
                found.checked = value
            }
        }
        if (objName === 'comparisonSurveyIds') {
            found = config.yearsQuestionWasAsked
                .find((bench) => bench.value === objValue)

            if (!isEmpty(found)) {
                found.checked = value
            }
        }
        if (objName === 'excludeNonNumeric' || objName === 'locked') {
            found = questionOptions.filters
                .find((option) => option.name === objName)

            if (!isEmpty(found)) {
                found.options[0].checked = value
            }
        }

        const obj = {
            name: objName,
            value: objValue,
            config,
        }
        this.updatePowerPointObj(obj)
    }

    getInputFeildValue = (value, updatable) => {
        updatable.removeBaseLimit = value
    }
}
export default PresentationStore
